import React from 'react'
import { Redirect, Switch, useRouteMatch } from 'react-router-dom'
import { SecureRoute } from '@okta/okta-react'
import ErrorBoundary from '../../molecules/ErrorBoundary'
import FormattingProvider from '../../organisms/FormattingProvider'
import { useHideSideMenuEffect } from '../../../redux/slices/appContext'
import NotAvailable from '../../organisms/NotAvailable'
import { useIsDenaliActive } from '../../../hooks/featureChecks'
import PolicyAuthorization from '../../atoms/PolicyAuthorization'
import IndexView from './IndexView'
import IntegratorHome from './Integrator/IntegratorHome'
import AlertsHome from './Alerts/AlertsHome'
import AnalysisHome from './Analysis/AnalysisHome'
import IngestionHome from './Ingestion/IngestionHome'
import OrchestratorHome from './Orchestrator/OrchestratorHome'
import IntegrationDomains from './Integrator/Domains'
import IntegrationJobs from './Integrator/IntegrationJobs'
import BatchList from './Integrator/BatchList'
import BatchDetails from './Integrator/BatchDetails'
import DenaliProvider from './DenaliProvider'
import QueryHome from './Integrator/Query'
import ViewsHome from './Analysis/Views/ViewsHome'
import ViewDetails from './Analysis/Views/ViewDetails/ViewDetails'
import QueryDenaliReport from './Analysis/Views/QueryDenaliReport'
import PipelinesHome from './Analysis/Pipelines/PipelinesHome'
import EditPipeline from './Analysis/Pipelines/EditPipeline'
import QueryBlueprintsHome from './Analysis/QueryBlueprints/QueryBlueprintsHome'
import QueryBlueprintDetails from './Analysis/QueryBlueprints/QueryBlueprintDetails'
import ParseDqlAnalysisTool from './Analysis/QueryBlueprints/ParseDqlAnalysisTool'
import CatalogHome from './Catalog/CatalogHome'
import EventLogsHome from './Catalog/EventLogs/EventLogsHome'
import DataSourceList from './Catalog/DataSourceList/DataSourceList'
import DataSourceDetails from './Catalog/DataSourceDetails'
import ConnectionsList from './Integrator/ConnectionsList'
import SourceDetailsHome from './Integrator/SourceDetails/SourceDetailsHome'
import JobDetailsHome from './Integrator/JobDetails/JobDetailsHome'
import SystemJobsHome from './Ingestion/SystemJobs/SystemJobsHome'
import ListJobGroups from './Ingestion/SystemJobGroups/ListJobGroups'
import JobGroupDetails from './Ingestion/SystemJobGroups/JobGroupDetails'
import OrionSystemHome from './OrionSystem/OrionSystemHome'
import QueryToolHome from './Analysis/QueryTool/QueryToolHome'
import SavedQueriesHome from './Analysis/SavedQueries/SavedQueriesHome'
import DenaliAIIndexView from './DenaliAI/IndexView'
import DenaliAISkills from './DenaliAI/Skills'
import DataMartsHome from './Analysis/DataMarts/DataMartsHome'
import EditDataMart from './Analysis/DataMarts/EditDataMart'
import ScheduleManagement from './Scheduler/ScheduleManagement'
import WorkflowManagement from './Orchestrator/Workflows/WorkflowManagement'
import ComponentBlueprintsHome from './ComponentBlueprints/ComponentBlueprintsHome'
import ComponentBlueprintDetails from './ComponentBlueprints/ComponentBlueprintDetails'
import DelegatedIdentityManagement from './DelegatedIdentities/DelegatedIdentityManagement'
import SmartEndpointsHome from './Analysis/SmartEndpoints/SmartEndpointsHome'
import SmartEndpointWorkspace from './Analysis/SmartEndpoints/SmartEndpointWorkspace'
import ActionTemplateDetails from './Catalog/ActionTemplateDetails'
import ProvisionedTargetList from './Integrator/ProvisionedTargets/ProvisionedTargetList'
import ProvisionedTargetDetails from './Integrator/ProvisionedTargets/ProvisionedTargetDetails'

const DenaliIndex = () => {
  const { path } = useRouteMatch()
  useHideSideMenuEffect([])
  const isDenaliActive = useIsDenaliActive()

  if (!isDenaliActive) {
    return <NotAvailable />
  }

  return (
    <ErrorBoundary name='Denali'>
      <FormattingProvider>
        <DenaliProvider>
          <Switch>
            <SecureRoute path={path} exact>
              <IndexView root={path} />
            </SecureRoute>
            <SecureRoute path={`${path}/alerts`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <AlertsHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <AnalysisHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/views`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <ViewsHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/views/:viewId`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <ViewDetails root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/views/:reportId/query`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <QueryDenaliReport root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/pipelines`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <PipelinesHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/pipelines/new`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <EditPipeline root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/catalog/saved-queries`} exact>
              <PolicyAuthorization policy='admin_denali_catalog_query'>
                <SavedQueriesHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/catalog/query`} exact>
              <PolicyAuthorization policy='admin_denali_catalog_query'>
                <QueryToolHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/pipelines/:pipelineId`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <EditPipeline root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/query-blueprints`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <QueryBlueprintsHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/query-blueprints/parse-dql`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <ParseDqlAnalysisTool root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/query-blueprints/:blueprintId`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <QueryBlueprintDetails root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/smart-endpoints`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <SmartEndpointsHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/smart-endpoints/new`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <SmartEndpointWorkspace />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/smart-endpoints/:smartEndpointId`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <SmartEndpointWorkspace />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/data-marts`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <DataMartsHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/data-marts/new`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <EditDataMart root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/analysis/data-marts/:timeSeriesId`}>
              <PolicyAuthorization policy='admin_denali_internal'>
                <EditDataMart root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/catalog`} exact>
              <Redirect to={`${path}/catalog/list`} />
            </SecureRoute>
            <SecureRoute path={`${path}/catalog/list`} exact>
              <PolicyAuthorization policy='admin_denali_catalog'>
                <CatalogHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/catalog/list/:type`} exact>
              <PolicyAuthorization policy='admin_denali_catalog'>
                <CatalogHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/catalog/sources`} exact>
              <PolicyAuthorization policy='admin_denali_catalog'>
                <DataSourceList root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/catalog/sources/:datasourceId`} exact>
              <PolicyAuthorization policy='admin_denali_catalog'>
                <DataSourceDetails root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/catalog/s/:datasourceId`} exact>
              <PolicyAuthorization policy='admin_denali_catalog'>
                <DataSourceDetails root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/catalog/actions/:actionTemplateId`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <ActionTemplateDetails root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/catalog/events`} exact>
              <PolicyAuthorization policy='admin_denali_catalog'>
                <EventLogsHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/ingestion`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <IngestionHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/ingestion/system-jobs`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <SystemJobsHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/ingestion/system-jobs/groups`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <ListJobGroups root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/ingestion/system-jobs/groups/:jobGroupCode`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <JobGroupDetails root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/integrator`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <IntegratorHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/integrator/connections`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <ConnectionsList root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/integrator/connections/:connectionCode`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <ConnectionsList root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/integrator/domains`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <IntegrationDomains root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/integrator/sources/:integrationSourceId`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <SourceDetailsHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/integrator/jobs/:jobConfigurationId`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <JobDetailsHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/integrator/jobs/:jobConfigurationId/query`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <QueryHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/integrator/jobs`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <IntegrationJobs root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/integrator/batches`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <BatchList root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/integrator/batches/:batchId`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <BatchDetails root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/integrator/targets`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <ProvisionedTargetList root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/integrator/targets/:provisionedTargetTableId`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <ProvisionedTargetDetails root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/orion-system`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <OrionSystemHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/component-blueprints`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <ComponentBlueprintsHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/component-blueprints/:blueprintId`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <ComponentBlueprintDetails root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/orchestration`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <OrchestratorHome root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/ai`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <DenaliAIIndexView root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/ai/skills`} exact>
              <PolicyAuthorization policy='admin_denali_internal'>
                <DenaliAISkills root={path} />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/orchestration/workflows`}>
              <PolicyAuthorization policy='admin_denali_internal'>
                <WorkflowManagement />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/orchestration/scheduler`}>
              <PolicyAuthorization policy='admin_denali_internal'>
                <ScheduleManagement />
              </PolicyAuthorization>
            </SecureRoute>
            <SecureRoute path={`${path}/orchestration/identities`}>
              <PolicyAuthorization policy='admin_denali_internal'>
                <DelegatedIdentityManagement />
              </PolicyAuthorization>
            </SecureRoute>
          </Switch>
        </DenaliProvider>
      </FormattingProvider>
    </ErrorBoundary>
  )
}

export default DenaliIndex
